import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreatePaymentLink } from "../../../pages/Payment/CreatePaymentLink/CreatePaymentLink/Steps/CreatePaymentLinkSchema";
import { createPaymentAccountFormatter, formatCreatePaymentLinkBody } from "../../formatters/paymentFormatter";

import {
  connectStripePaymentAccount,
  createPaymentAccount,
  createPaymentLink,
  deletePaymentLink,
  updatePaymentAccount,
} from "../../services/payment";

export const useCreatePaymentLink = (onSuccess?: (link: string) => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreatePaymentLink) => {
      const formattedBody = formatCreatePaymentLinkBody(body);
      return await createPaymentLink(formattedBody);
    },
    onSuccess: (link) => {
      queryClient.invalidateQueries({ queryKey: ["Payment", "PaymentLinks"] });
      if (onSuccess) {
        onSuccess(link);
      }
    },
    onError: () => {
      if (onError) onError();
    },
  });
};

export const useConnectStripPaymentAccount = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stripeConnectedAccountId: string) => {
      return await connectStripePaymentAccount(stripeConnectedAccountId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Payment", "PaymentAccounts"] });
      if (onSuccess) onSuccess();
    },
    onError: () => {
      if (onError) onError();
    },
  });
};

export const useDeletePaymentLink = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkId: number) => {
      return await deletePaymentLink({ linkId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Payment", "PaymentLinks"] });
      if (onSuccess) onSuccess();
    },
    onError: () => {
      if (onError) onError();
    },
  });
};

export const useCreatePaymentAccount = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: any) => {
      return await createPaymentAccount(createPaymentAccountFormatter(body));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Payment"] });
      if (onSuccess) onSuccess();
    },
    onError: () => {
      if (onError) onError();
    },
  });
};

export const useUpdatePaymentAccount = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payoutAccountId, body }: { payoutAccountId: number; body: any }) => {
      return await updatePaymentAccount(payoutAccountId, createPaymentAccountFormatter(body));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Payment"] });
      if (onSuccess) onSuccess();
    },
    onError: () => {
      if (onError) onError();
    },
  });
};
